import { BathProductPage, BathProductConfig } from "@/components/bath/BathProductPage";
import { Rem7HydraSpecPDFButton } from "@/components/bath/Rem7HydraSpecPDFButton";
import { Rem7HydraFeatureShowcase, FeatureModule } from "@/components/bath/Rem7HydraFeatureShowcase";
import { Rem7HydraSpecsPanel, SpecGroup, DimensionMetric, InBoxItem } from "@/components/bath/Rem7HydraSpecsPanel";
import { Sparkles, ShieldCheck, Wrench, Layers, TrendingUp, CheckCircle2, Home, Building2, Hotel, Waves, Radio, Droplets, Ruler, Zap, Gauge, Package, Settings2, Speaker, Lightbulb, ShowerHead, Cog, Armchair } from "lucide-react";

/**
 * /:lang/bath-sanitary/rem7-whirlpool-deluxe
 * REM7 Hydra Deluxe — 1700×900×670mm rectangular acrylic whirlpool with
 * touch digital control system (water sensor, ozone, FM radio, loudspeaker),
 * 1.5HP surfing massage, big waterfall, chromotherapy LED bubble bath,
 * 3-side apron. The fully-loaded flagship of the REM7 Hydra programme.
 */
const config: BathProductConfig = {
  slug: "rem7-whirlpool-deluxe",
  category: "Whirlpool Massage Bathtub",
  productName: "REM7 Hydra Deluxe Acrylic Whirlpool Massage Bathtub",
  shortName: "REM7 Hydra Deluxe",
  sku: "REM7-HYDRA-7176D",
  schemaCategory: "Bathtubs > Whirlpool > Deluxe Acrylic with Touch Control",
  metaTitle: "REM7 Hydra Deluxe Whirlpool Bathtub | 32-Jet Hydromassage",
  metaDescription:
    "16 hydro + 16 LED bubble jets, ozone disinfection, FM radio, waterfall and digital touch control. Specified for 5★ hotels and primary suites.",
  heroEyebrow: "Project Solutions · Deluxe Whirlpool with Touch Digital Control",
  heroHeadline: (
    <>
      Touch-Control Whirlpool with{" "}
      <span className="bg-gradient-to-r from-amber-300 via-amber-400 to-amber-200 bg-clip-text text-transparent">
        Ozone, FM Radio & Big Waterfall
      </span>
    </>
  ),
  heroIntro:
    "The fully-loaded flagship of the REM7 Hydra programme. A 1700 × 900 mm rectangular acrylic whirlpool with a touch digital control system featuring water sensor, ozone disinfection, FM radio and built-in loudspeaker, paired with a 1.5HP surfing-massage pump, big decorative waterfall, chromotherapy LED bubble bath and a full 3-side apron — designed as the signature spa bathtub for European luxury suites and branded residences.",
  premiumSummary:
    "Built around 100% pure sanitary-grade PMMA acrylic, a 1.5HP whirlpool pump driving 4 large + 8 small ultra-thin water jets, a 300W bubble pump with 8 LED bubble jets, a big decorative waterfall and a touch digital control with water sensor, ozone disinfection, FM radio and loudspeaker. Hot/cold mixer, diverter, hand shower, water intake faucet and full 3-side apron — a complete deluxe spa bathtub for European luxury projects.",
  galleryImages: [
    {
      src: "/images/bath/whirlpool-deluxe/hero.jpg",
      alt: "REM7 Hydra Deluxe 1700mm acrylic whirlpool bathtub with touch control and waterfall",
      title: "REM7 Hydra Deluxe — Hero",
      caption: "Hero · Deluxe whirlpool with touch control",
      description:
        "Hero of the REM7 Hydra Deluxe 1700 × 900 × 670 mm acrylic whirlpool massage bathtub with touch digital control, big waterfall and chromotherapy LED bubble jets.",
    },
    {
      src: "/images/bath/whirlpool-deluxe/angle.jpg",
      alt: "REM7 Hydra Deluxe whirlpool three-quarter angle view",
      title: "REM7 Hydra Deluxe — Angle View",
      caption: "Angle · Rectangular deluxe format",
      description:
        "Three-quarter angle of the REM7 Hydra Deluxe whirlpool bathtub showing the 1700 × 900 mm rectangular footprint and full 3-side apron.",
    },
    {
      src: "/images/bath/whirlpool-deluxe/controls.jpg",
      alt: "REM7 Hydra Deluxe touch digital control system with FM radio and ozone",
      title: "REM7 Hydra Deluxe — Touch Digital Control",
      caption: "Detail · Touch control · FM radio · Ozone",
      description:
        "Macro of the REM7 Hydra Deluxe touch digital control system — water sensor, ozone disinfection, FM radio and integrated loudspeaker, with whirlpool and air adjusting switches.",
    },
    {
      src: "/images/bath/whirlpool-deluxe/jets.jpg",
      alt: "REM7 Hydra Deluxe ultra-thin surfing massage jets 4 large 8 small",
      title: "REM7 Hydra Deluxe — Surfing Jets",
      caption: "Detail · 1.5HP surfing massage",
      description:
        "Detail of the ultra-thin surfing-massage jet array on the REM7 Hydra Deluxe — 4 large + 8 small water jets driven by a 1.5HP whirlpool pump with run-dry protection.",
    },
    {
      src: "/images/bath/whirlpool-deluxe/waterfall.jpg",
      alt: "REM7 Hydra Deluxe big decorative waterfall feature",
      title: "REM7 Hydra Deluxe — Big Waterfall",
      caption: "Feature · Big waterfall",
      description:
        "Detail of the big decorative waterfall on the REM7 Hydra Deluxe, switchable between waterfall flow and full water-massage mode.",
    },
    {
      src: "/images/bath/whirlpool-deluxe/led.jpg",
      alt: "REM7 Hydra Deluxe chromotherapy LED bubble jets",
      title: "REM7 Hydra Deluxe — LED Chromotherapy",
      caption: "Detail · 8 LED bubble jets",
      description:
        "Chromotherapy detail of the REM7 Hydra Deluxe — 300W bubble pump powering 8 colourful LED bubble jets for a full chromotherapy spa experience.",
    },
    {
      src: "/images/bath/whirlpool-deluxe/detail.jpg",
      alt: "REM7 Hydra Deluxe acrylic shell and headrest detail",
      title: "REM7 Hydra Deluxe — Shell Detail",
      caption: "Detail · PMMA acrylic + pillows",
      description:
        "Close-up of the high-gloss PMMA acrylic shell and dual white ergonomic headrests on the REM7 Hydra Deluxe whirlpool bathtub.",
    },
    {
      src: "/images/bath/whirlpool-deluxe/apron.jpg",
      alt: "REM7 Hydra Deluxe 3-side apron with mixer and hand shower",
      title: "REM7 Hydra Deluxe — 3-Side Apron",
      caption: "Detail · 3-side apron · plumbing pack",
      description:
        "Detail of the integrated 3-side apron on the REM7 Hydra Deluxe with hot/cold mixer, diverter, hand shower and water intake faucet.",
    },
    {
      src: "/images/bath/whirlpool-deluxe/lifestyle.jpg",
      alt: "REM7 Hydra Deluxe whirlpool installed in luxury hotel suite",
      title: "REM7 Hydra Deluxe — Lifestyle",
      caption: "Lifestyle · Hotel spa suite",
      description:
        "Lifestyle scene: REM7 Hydra Deluxe whirlpool installed as the centrepiece of a luxury hotel spa suite with diverter mixer and hand shower.",
    },
  ],
  specs: [
    { label: "Dimensions", value: "1700 × 900 × 670 mm (L × W × H)" },
    { label: "Format", value: "Rectangular whirlpool · single bather · 3-side apron" },
    { label: "Shell Material", value: "100% Sanitary-grade PMMA Acrylic (high-gloss, scratch / stain / fade resistant)" },
    { label: "Reinforcement", value: "Alkali-free fibreglass + resin + wood reinforcement" },
    { label: "Hydro Massage", value: "1.5HP pump · 4 large + 8 small ultra-thin surfing jets" },
    { label: "Air / Bubble Bath", value: "300W bubble pump · 8 LED bubble jets" },
    { label: "Waterfall", value: "Big decorative waterfall with shift switch" },
    { label: "Control System", value: "Touch digital control · water sensor · ozone disinfection · FM radio · loudspeaker" },
    { label: "Pump Protection", value: "Run-without-water protection on hydro pump" },
    { label: "Plumbing", value: "Hot/cold mixer · diverter · hand shower · water intake faucet · drainer with overflow" },
    { label: "Apron", value: "Integrated 3-side apron" },
    { label: "Pillows", value: "2 × white ergonomic headrests" },
    { label: "Support / Feet", value: "Stainless steel frame · pre-levelled height-adjustable feet" },
    { label: "Certification", value: "CE · SGS · cUPC · ISO 9001" },
    { label: "Warranty", value: "5 years shell · 2 years components" },
    { label: "MOQ", value: "From 5 units · Bulk pricing 20+" },
    { label: "Lead Time", value: "20–30 working days · 40ft container ≈ 28 units" },
  ],
  quickFeatures: [
    "1700 × 900 × 670 mm rectangular deluxe format",
    "Touch digital control · water sensor · ozone disinfection",
    "Built-in FM radio + loudspeaker for spa audio",
    "1.5HP hydro pump · 12 ultra-thin surfing jets (4 large + 8 small)",
    "Big decorative waterfall with shift switch",
    "300W air pump · 8 chromotherapy LED bubble jets",
    "Run-without-water pump protection",
    "Full 3-side apron · hot/cold mixer · hand shower included",
    "100% sanitary-grade PMMA acrylic · CE / SGS / cUPC",
  ],
  productMaterial: "100% Sanitary-grade PMMA Acrylic with fibreglass + resin + wood reinforcement",
  additionalProperties: [
    { name: "Length", value: "1700", unitText: "MMT" },
    { name: "Width", value: "900", unitText: "MMT" },
    { name: "Height", value: "670", unitText: "MMT" },
    { name: "Hydro Pump Power", value: "1.5", unitText: "HP" },
    { name: "Air Pump Power", value: "300", unitText: "WTT" },
    { name: "Voltage", value: "220V / 50Hz" },
    { name: "Bather Capacity", value: "1" },
    { name: "Hydro Jets", value: "12 (4 large + 8 small)" },
    { name: "LED Bubble Jets", value: "8" },
    { name: "Control Type", value: "Touch digital · ozone · FM radio · loudspeaker" },
    { name: "Waterfall", value: "Big decorative waterfall" },
    { name: "Apron Sides", value: "3" },
    { name: "Certification", value: "CE, SGS, cUPC, ISO 9001" },
    { name: "Shell Warranty", value: "5 years" },
    { name: "Component Warranty", value: "2 years" },
  ],
  whyChoose: [
    {
      icon: Radio,
      title: "Touch digital control with FM radio & ozone",
      body: "A single touch digital panel manages whirlpool, air, waterfall and lighting modes — with built-in water sensor, ozone disinfection, FM radio and loudspeaker for an integrated spa-audio experience.",
    },
    {
      icon: Waves,
      title: "Surfing massage + big waterfall",
      body: "A 1.5HP whirlpool pump drives a true surfing-style hydro massage through 12 ultra-thin jets, while the big decorative waterfall doubles as a sensory feature and a high-flow refill — switchable from a single shift switch.",
    },
    {
      icon: Sparkles,
      title: "Deluxe rectangular format",
      body: "The 1700 × 900 mm rectangular footprint with full 3-side apron is engineered to drop into European primary suites and resort villa bathrooms as a complete signature centrepiece — no extra cladding required.",
    },
    {
      icon: TrendingUp,
      title: "Premium ADR for resort & villa rooms",
      body: "Whirlpool suites with touch control, ozone disinfection and integrated audio consistently command higher nightly rates and longer minimum stays. The Deluxe is engineered as a project-grade hero tub for that segment.",
    },
    {
      icon: ShieldCheck,
      title: "Sanitary-grade durability",
      body: "100% pure PMMA acrylic, fibreglass + resin + wood reinforcement, run-dry pump protection and stainless support frame. CE / SGS / cUPC certified for residential and hospitality.",
    },
    {
      icon: Wrench,
      title: "Specifier-friendly installation",
      body: "Standard EU plumbing rough-in with mixer, diverter, hand shower and water intake faucet. Integrated drainer with overflow and pre-levelled adjustable feet keep site work straightforward for the trade.",
    },
    {
      icon: Layers,
      title: "Trade pricing, project margin",
      body: "Hospitality-grade deluxe whirlpool aesthetics without the European brand premium — structured trade pricing protects developer and contractor margins on phased rollouts.",
    },
    {
      icon: CheckCircle2,
      title: "Coherent REM7 Hydra programme",
      body: "Specify alongside the REM7 Hydra (1850 mm), Hydra Compact (1800 mm) and Hydra Surf (1500 mm corner) for a consistent whirlpool language across primary, standard and signature suites.",
    },
  ],
  perfectFor: [
    { icon: Hotel, title: "Resort Hotels", body: "Spa suites where the bathtub is the photographable hero with touch control and audio." },
    { icon: Home, title: "Branded Residences", body: "Primary suites wanting a fully-loaded deluxe whirlpool centrepiece." },
    { icon: Building2, title: "Private Villas", body: "Owner-occupied villas wanting ozone-disinfected daily-use whirlpool with FM radio." },
    { icon: Wrench, title: "Spa Renovations", body: "Bathroom renovations replacing a tired tub with a touch-controlled deluxe whirlpool." },
  ],
  faqs: [
    {
      question: "How is the REM7 Hydra Deluxe different from the standard REM7 Hydra?",
      answer:
        "The Deluxe is the touch-controlled, fully-loaded variant of the REM7 Hydra programme. It runs the same 1.5HP whirlpool philosophy in a 1700 × 900 × 670 mm rectangular format, but adds a touch digital control system with water sensor, ozone disinfection, FM radio, loudspeaker and a big decorative waterfall — engineered for European luxury suites that want spa-audio and self-disinfecting features.",
    },
    {
      question: "What does the touch digital control include?",
      answer:
        "The single touch panel manages whirlpool, air whirlpool and waterfall / water-massage modes, with built-in water sensor for safety, ozone disinfection for the recirculation circuit, FM radio reception and a loudspeaker for spa audio.",
    },
    {
      question: "What does the surfing massage system include?",
      answer:
        "A 1.5HP whirlpool pump drives 4 large + 8 small ultra-thin water jets configured for a surfing-style hydro massage. A separate 300W air bubble pump powers 8 LED chromotherapy bubble jets, controlled independently from the air switch.",
    },
    {
      question: "How does the big waterfall work?",
      answer:
        "The big decorative waterfall is plumbed in series with the water-massage circuit and switchable via a single waterfall / water-massage shift switch. It functions both as a sensory feature during use and as a high-flow filling mode.",
    },
    {
      question: "Is the REM7 Hydra Deluxe CE / cUPC certified for European installation?",
      answer:
        "Yes. The Deluxe carries CE, SGS, cUPC and ISO 9001 certification, and is engineered around standard EU plumbing rough-in with hot/cold mixer, diverter, hand shower, water intake faucet and integrated drainer with overflow.",
    },
    {
      question: "What protections are built into the pump?",
      answer:
        "The 1.5HP whirlpool pump includes run-without-water protection — if the water level drops below the safety threshold, the pump cuts out automatically to prevent dry running and damage to the impeller.",
    },
    {
      question: "What is the warranty on the REM7 Hydra Deluxe?",
      answer:
        "Aureum VII Trading covers it with a 5-year shell warranty and a 2-year warranty on electrical and mechanical components (pumps, touch control, ozone module, FM radio, LED, mixer). Professional installation by a qualified plumber is required for warranty validity.",
    },
    {
      question: "What is the MOQ and how many units fit a 40ft container?",
      answer:
        "MOQ starts at 5 units, with bulk tiers from 20 units. Production lead time is 20–30 working days. Approximately 28 units of the Deluxe rectangular format fit a 40ft container.",
    },
  ],
  longDescription: [
    "The REM7 Hydra Deluxe is the fully-loaded flagship of the REM7 Hydra whirlpool programme — a 1700 × 900 × 670 mm rectangular acrylic whirlpool engineered to anchor a signature spa bathroom in branded residences, resort hotels and private villas across Europe.",
    "Its defining feature is a touch digital control system that consolidates whirlpool, air bubble, waterfall and lighting modes into a single panel, with built-in water sensor for safety, ozone disinfection for the recirculation circuit, FM radio reception and a loudspeaker for spa audio.",
    "Hydraulically, a 1.5HP whirlpool pump drives a true surfing-style water massage through 4 large and 8 small ultra-thin jets, paired with a big decorative waterfall that can be switched between sensory waterfall flow and full water-massage mode via a single shift switch.",
    "The bubble bath circuit runs in parallel: a 300W air pump powers 8 colourful LED bubble jets for chromotherapy, controlled independently from the air switch. Two ergonomic white headrests, a full 3-side apron and a complete plumbing package (hot/cold mixer, diverter, hand shower, water intake faucet, drainer with overflow) ship with every unit.",
    "The shell is cast from 100% high-gloss sanitary-grade PMMA acrylic, scratch / stain / fade resistant, and reinforced with alkali-free fibreglass, resin and structural wood. A run-without-water protection circuit safeguards the hydro pump, and a stainless support frame with pre-levelled adjustable feet keeps installation level on uneven floors.",
    "Specify the REM7 Hydra Deluxe in primary spa suites, alongside the REM7 Hydra (1850 mm rectangular), the REM7 Hydra Compact (1800 mm) and the REM7 Hydra Surf (1500 mm corner with waterfall), the ARIA Sculpt / Monolith / Diamond freestanding bathtubs, the VENA basin and vanity range, and the REM7 smart toilet flagship — a coherent Aureum VII bath programme for European luxury projects.",
  ],
};

const featureModules: FeatureModule[] = [
  {
    id: "control",
    icon: Radio,
    label: "Touch Console",
    tagline: "Module 01 · Control",
    title: "Touch digital console with ozone, FM radio & loudspeaker",
    description:
      "A single capacitive touch panel consolidates every system on the tub — whirlpool, air bubble, waterfall and lighting — with built-in water sensor, ozone disinfection of the recirculation circuit, FM radio reception and an integrated loudspeaker for a true spa-audio experience.",
    bullets: [
      "Capacitive touch interface · ultra-thin glass surface",
      "Water sensor for safe operation and dry-run protection",
      "Ozone disinfection module on the recirculation loop",
      "Built-in FM radio + loudspeaker for in-tub audio",
    ],
    image: { src: "/images/bath/whirlpool-deluxe/controls.jpg", alt: "REM7 Hydra Deluxe touch digital control panel" },
    metric: { value: "1-Touch", label: "Unified Spa Control" },
  },
  {
    id: "hydro",
    icon: Waves,
    label: "Surfing Massage",
    tagline: "Module 02 · Hydro Massage",
    title: "1.5HP surfing massage with 12 ultra-thin jets",
    description:
      "A 1.5HP whirlpool pump drives a true surfing-style hydro massage through 4 large + 8 small ultra-thin jets, configured for back, lumbar and leg coverage. Run-without-water protection cuts power if the level drops below the safety threshold.",
    bullets: [
      "1.5HP whirlpool pump · run-dry protection circuit",
      "12 ultra-thin water jets (4 large + 8 small)",
      "Adjustable air whirlpool switch for massage intensity",
      "Back / lumbar / leg coverage in a single bathing position",
    ],
    image: { src: "/images/bath/whirlpool-deluxe/jets.jpg", alt: "REM7 Hydra Deluxe surfing massage jet array" },
    metric: { value: "12 Jets", label: "1.5HP Surfing Massage" },
  },
  {
    id: "waterfall",
    icon: Droplets,
    label: "Big Waterfall",
    tagline: "Module 03 · Waterfall",
    title: "Big decorative waterfall with shift switch",
    description:
      "The big decorative waterfall is plumbed in series with the water-massage circuit and switched from a single waterfall / water-massage shift switch — doubling as a sensory feature during use and a high-flow filling mode at the start of the bath.",
    bullets: [
      "Single shift switch · waterfall ↔ full water massage",
      "High-flow filling mode reduces fill time noticeably",
      "Sensory waterfall feature for premium spa experience",
      "Clean satin chrome spout that frames the bath",
    ],
    image: { src: "/images/bath/whirlpool-deluxe/waterfall.jpg", alt: "REM7 Hydra Deluxe big decorative waterfall feature" },
    metric: { value: "1 Switch", label: "Waterfall ↔ Massage" },
  },
  {
    id: "led",
    icon: Sparkles,
    label: "LED Bubble",
    tagline: "Module 04 · Bubble Bath",
    title: "300W bubble bath with 8 chromotherapy LED jets",
    description:
      "A separate 300W air pump powers 8 colourful LED bubble jets for chromotherapy, controlled independently from the touch panel — so guests can run a soft bubble bath with mood lighting without engaging the high-pressure surfing massage.",
    bullets: [
      "300W dedicated air pump · whisper-quiet operation",
      "8 chromotherapy LED bubble jets · floor-mounted",
      "Independent control from the surfing massage circuit",
      "Soft bath / mood lighting mode for evening use",
    ],
    image: { src: "/images/bath/whirlpool-deluxe/led.jpg", alt: "REM7 Hydra Deluxe chromotherapy LED bubble jets" },
    metric: { value: "8 LEDs", label: "Chromotherapy Bath" },
  },
  {
    id: "plumbing",
    icon: Wrench,
    label: "Plumbing Pack",
    tagline: "Module 05 · Plumbing",
    title: "Complete EU plumbing package out-of-the-box",
    description:
      "Every Hydra Deluxe ships with a complete European plumbing pack — hot/cold mixer, diverter, hand shower, water intake faucet, drainer with overflow — so the trade can install with standard rough-in and no exotic parts.",
    bullets: [
      "Hot / cold mixer + diverter mixer included",
      "Hand shower + dedicated water intake faucet",
      "Backwater drainer with integrated overflow",
      "Pre-levelled, height-adjustable stainless feet",
    ],
    image: { src: "/images/bath/whirlpool-deluxe/apron.jpg", alt: "REM7 Hydra Deluxe 3-side apron with mixer and hand shower" },
    metric: { value: "EU-Ready", label: "Standard Rough-In" },
  },
  {
    id: "build",
    icon: ShieldCheck,
    label: "Build Quality",
    tagline: "Module 06 · Materials",
    title: "Sanitary-grade PMMA acrylic on stainless frame",
    description:
      "100% pure sanitary-grade PMMA acrylic shell — high-gloss, scratch / stain / fade resistant — reinforced with alkali-free fibreglass, resin and structural wood, mounted on a stainless steel frame with pre-levelled feet and CE / SGS / cUPC certification.",
    bullets: [
      "100% sanitary-grade PMMA acrylic shell",
      "Fibreglass + resin + wood reinforcement layers",
      "Stainless steel structural support frame",
      "CE · SGS · cUPC · ISO 9001 certified",
    ],
    image: { src: "/images/bath/whirlpool-deluxe/detail.jpg", alt: "REM7 Hydra Deluxe acrylic shell and headrest detail" },
    metric: { value: "5-Year", label: "Shell Warranty" },
  },
];

const dimensionMetrics: DimensionMetric[] = [
  { label: "Length", value: "1700", unit: "mm" },
  { label: "Width", value: "900", unit: "mm" },
  { label: "Height", value: "670", unit: "mm" },
  { label: "Capacity", value: "1", unit: "Bather" },
];

const specGroups: SpecGroup[] = [
  {
    id: "dimensions",
    icon: Ruler,
    title: "Dimensions & Format",
    rows: [
      { label: "Overall Size", value: "1700 × 900 × 670 mm" },
      { label: "Format", value: "Rectangular · single bather" },
      { label: "Apron", value: "Integrated 3-side apron" },
      { label: "Bathing Position", value: "Reclined · 2 headrest pillows" },
      { label: "Net Weight (approx.)", value: "≈ 95 kg" },
    ],
  },
  {
    id: "hydromassage",
    icon: Waves,
    title: "Hydro Massage System",
    rows: [
      { label: "Hydro Pump", value: "1.5 HP whirlpool pump" },
      { label: "Surfing Jets", value: "12 ultra-thin (4 large + 8 small)" },
      { label: "Pump Protection", value: "Run-without-water cut-off circuit" },
      { label: "Jet Adjustment", value: "Air whirlpool adjusting switch" },
      { label: "Massage Coverage", value: "Back · lumbar · legs" },
    ],
  },
  {
    id: "bubble",
    icon: Sparkles,
    title: "Air Bubble & LED",
    rows: [
      { label: "Air Pump", value: "300 W bubble pump" },
      { label: "LED Bubble Jets", value: "8 chromotherapy LEDs" },
      { label: "Operating Mode", value: "Independent of hydro circuit" },
      { label: "Lighting", value: "Multi-colour chromotherapy" },
    ],
  },
  {
    id: "control",
    icon: Radio,
    title: "Touch Digital Control",
    rows: [
      { label: "Interface", value: "Capacitive touch panel" },
      { label: "Water Sensor", value: "Yes · safe-operation lockout" },
      { label: "Disinfection", value: "Ozone module on recirculation" },
      { label: "Audio", value: "FM radio + integrated loudspeaker" },
      { label: "Power Supply", value: "220 V / 50 Hz" },
    ],
  },
  {
    id: "waterfeature",
    icon: Droplets,
    title: "Water Features & Plumbing",
    rows: [
      { label: "Waterfall", value: "Big decorative waterfall + shift switch" },
      { label: "Mixer", value: "Hot/cold mixer + diverter mixer" },
      { label: "Hand Shower", value: "Included with hose" },
      { label: "Filling", value: "Dedicated water intake faucet" },
      { label: "Drain", value: "Backwater drainer with overflow" },
    ],
  },
  {
    id: "build",
    icon: ShieldCheck,
    title: "Build, Certification & Logistics",
    rows: [
      { label: "Shell Material", value: "100% sanitary-grade PMMA acrylic" },
      { label: "Reinforcement", value: "Fibreglass + resin + wood" },
      { label: "Support Frame", value: "Stainless steel · adjustable feet" },
      { label: "Certification", value: "CE · SGS · cUPC · ISO 9001" },
      { label: "Warranty", value: "5 yrs shell · 2 yrs components" },
      { label: "MOQ / Container", value: "From 5 units · ≈ 28 / 40ft" },
      { label: "Lead Time", value: "20–30 working days" },
    ],
  },
];

const inBoxItems: InBoxItem[] = [
  { icon: Settings2, label: "Touch digital control panel", qty: "1 unit · pre-installed" },
  { icon: Cog, label: "1.5 HP whirlpool pump", qty: "1 unit · with run-dry guard" },
  { icon: Waves, label: "Ultra-thin surfing jets", qty: "12 jets (4L + 8S)" },
  { icon: Sparkles, label: "300 W air bubble pump", qty: "1 unit" },
  { icon: Lightbulb, label: "Chromotherapy LED bubble jets", qty: "8 LEDs" },
  { icon: Droplets, label: "Big decorative waterfall", qty: "1 spout · with shift switch" },
  { icon: ShowerHead, label: "Hot/cold mixer + diverter", qty: "1 set + hand shower" },
  { icon: ShowerHead, label: "Water intake faucet", qty: "1 unit · brushed chrome" },
  { icon: Package, label: "Backwater drainer + overflow", qty: "1 set · pre-fitted" },
  { icon: Armchair, label: "Ergonomic headrest pillows", qty: "2 units · white" },
  { icon: Wrench, label: "Stainless steel support frame", qty: "1 set · adjustable feet" },
  { icon: Speaker, label: "FM radio + loudspeaker module", qty: "1 unit · integrated" },
];

const Rem7WhirlpoolDeluxe = () => (
  <BathProductPage
    config={{
      ...config,
      specsPdfButton: (
        <Rem7HydraSpecPDFButton specs={config.specs} features={config.quickFeatures ?? []} />
      ),
      featureShowcase: (
        <Rem7HydraFeatureShowcase
          shortName={config.shortName}
          modules={featureModules}
        />
      ),
      specsPanel: (
        <Rem7HydraSpecsPanel
          shortName={config.shortName}
          productName={config.productName}
          sku={config.sku}
          heroImage={{
            src: "/images/bath/whirlpool-deluxe/angle.jpg",
            alt: "REM7 Hydra Deluxe whirlpool dimensional reference view",
          }}
          dimensions={dimensionMetrics}
          groups={specGroups}
          inBox={inBoxItems}
          certifications={["CE", "SGS", "cUPC", "ISO 9001"]}
          pdfButton={
            <Rem7HydraSpecPDFButton specs={config.specs} features={config.quickFeatures ?? []} />
          }
        />
      ),
    }}
  />
);
export default Rem7WhirlpoolDeluxe;
